import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AclApdApiService } from '@angular-workspace/services';

export interface RequestStatusItem {
  id: string | number;
  label: string;
  count: number;
  status: 'approved' | 'rejected' | 'withdrawn' | 'pending';
  route?: string;
  href?: string;
}

@Component({
  selector: 'app-request-status',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './request-status.component.html',
  styleUrl: './request-status.component.scss',
})
export class RequestStatusComponent implements OnInit {
  @Input() title: string = 'My Request';
  @Input() items: RequestStatusItem[] = [];

  constructor(
    private sanitizer: DomSanitizer,
    private aclApdApiService: AclApdApiService
  ) {}

  defaultItems: RequestStatusItem[] = [
    {
      id: 1,
      label: 'Approved',
      count: 0,
      status: 'approved',
      route: '/consumer/requests?status=approved'
    },
    {
      id: 2,
      label: 'Rejected',
      count: 0,
      status: 'rejected',
      route: '/consumer/requests?status=rejected'
    },
    {
      id: 3,
      label: 'Withdrawn',
      count: 0,
      status: 'withdrawn',
      route: '/consumer/requests?status=withdrawn'
    },
    {
      id: 4,
      label: 'Pending',
      count: 0,
      status: 'pending',
      route: '/consumer/requests?status=pending'
    }
  ];

  ngOnInit(): void {
    this.loadAccessRequestCounts();
  }

  async loadAccessRequestCounts(): Promise<void> {
    try {
      // Fetch all access requests with a large page size to get all requests
      // Alternatively, we could use a count API if available
      const response = await this.aclApdApiService.getUserAccessRequests(1, 1000);
      
      // Extract the data array from response
      let requests: any[] = [];
      if (response && response['result'] && Array.isArray(response['result'])) {
        requests = response['result'];
      } else if (Array.isArray(response)) {
        requests = response;
      }

      // Count requests by status
      const statusCounts = {
        approved: 0,
        rejected: 0,
        withdrawn: 0,
        pending: 0
      };

      requests.forEach((request: any) => {
        const status = (request.requestStatus || request.status || '').toLowerCase();
        if (status === 'approved' || status === 'granted') {
          statusCounts.approved++;
        } else if (status === 'rejected') {
          statusCounts.rejected++;
        } else if (status === 'withdrawn') {
          statusCounts.withdrawn++;
        } else if (status === 'pending' || status === 'active') {
          statusCounts.pending++;
        }
      });

      // Update defaultItems with counts
      this.defaultItems = this.defaultItems.map(item => ({
        ...item,
        count: statusCounts[item.status] || 0
      }));
    } catch (error) {
      console.error('Error loading access request counts:', error);
      // On error, keep default counts (0)
    }
  }

  get displayItems(): RequestStatusItem[] {
    return this.items.length > 0 ? this.items : this.defaultItems;
  }

  getStatusIcon(status: string): SafeHtml {
    let iconPath = '';
    switch (status) {
      case 'approved':
        iconPath = '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
        break;
      case 'rejected':
        iconPath = '<path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
        break;
      case 'withdrawn':
        iconPath = '<path d="M3 12L12 3L21 12M12 21V3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
        break;
      case 'pending':
        iconPath = '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>';
        break;
      default:
        iconPath = '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(iconPath);
  }

  getRoutePath(route: string): string {
    if (!route) return '';
    const queryIndex = route.indexOf('?');
    return queryIndex > -1 ? route.substring(0, queryIndex) : route;
  }

  getQueryParams(route: string): { [key: string]: string } {
    if (!route) return {};
    const queryIndex = route.indexOf('?');
    if (queryIndex === -1) return {};
    
    const queryString = route.substring(queryIndex + 1);
    const params: { [key: string]: string } = {};
    queryString.split('&').forEach(param => {
      const [key, value] = param.split('=');
      if (key && value) {
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    });
    return params;
  }
}

